import {
	browserSupportsWebAuthn,
	browserSupportsWebAuthnAutofill,
	startAuthentication,
} from '@simplewebauthn/browser';
import { Fingerprint, KeyRound, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getPasskeyOptions, loginWithPasskey, loginWithPassword } from '@/api/auth';
import { setAuthToken } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type LoginMode = 'passkey' | 'password';
type LoginState = 'idle' | 'loading' | 'success' | 'error';

interface LoginPageProps {
	onSuccess?: () => void;
	redirectTo?: string;
}

export function LoginPage({ onSuccess, redirectTo = '/' }: LoginPageProps) {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [mode, setMode] = useState<LoginMode>('passkey');
	const [state, setState] = useState<LoginState>('idle');
	const [error, setError] = useState<string | null>(null);
	const [supportsWebAuthn, setSupportsWebAuthn] = useState(true);
	const [supportsAutofill, setSupportsAutofill] = useState(false);

	const usernameInputRef = useRef<HTMLInputElement>(null);
	const conditionalAbortController = useRef<AbortController | null>(null);

	// Check WebAuthn support on mount
	useEffect(() => {
		const checkSupport = async () => {
			const webAuthnSupported = browserSupportsWebAuthn();
			setSupportsWebAuthn(webAuthnSupported);

			if (webAuthnSupported) {
				try {
					const autofillSupported = await browserSupportsWebAuthnAutofill();
					setSupportsAutofill(autofillSupported);
				} catch {
					setSupportsAutofill(false);
				}
			}

			// Focus username input on mount
			usernameInputRef.current?.focus();
		};

		checkSupport();

		// Cleanup: abort any conditional UI on unmount
		return () => {
			conditionalAbortController.current?.abort();
		};
	}, []);

	// Start conditional UI (passkey autofill) when username field is focused
	// This is triggered on page load for supporting browsers
	useEffect(() => {
		if (!supportsAutofill || !supportsWebAuthn) return;

		async function startConditionalUI() {
			try {
				// Abort any existing conditional UI request
				conditionalAbortController.current?.abort();
				conditionalAbortController.current = new AbortController();

				// We need to get options without a specific username for conditional UI
				// This will wait for the user to select a passkey from autofill
				const { options, error: optionsError } = await getPasskeyOptions('');

				if (!options || optionsError) {
					// For conditional UI, silently fail if we can't get options
					// The user can still manually enter username and click login
					console.debug('Conditional UI: could not get options:', optionsError);
					return;
				}

				const credential = await startAuthentication({
					optionsJSON: options,
					useBrowserAutofill: true,
				});

				// User selected a passkey from autofill
				// The userHandle contains the user ID, we need to complete the login
				setState('loading');

				// For conditional UI, we use the credential directly
				// The server should be able to identify the user from the credential
				const result = await loginWithPasskey('', credential);

				if (result.success && result.token) {
					setAuthToken(result.token);
					setState('success');
					if (onSuccess) {
						onSuccess();
					} else {
						window.location.href = redirectTo;
					}
				} else {
					setState('error');
					setError(result.error || 'Authentication failed');
				}
			} catch (err) {
				// AbortError is expected when component unmounts or new auth starts
				if (err instanceof Error && err.name === 'AbortError') {
					return;
				}
				// NotAllowedError happens when user cancels or conditional UI times out
				if (err instanceof Error && err.name === 'NotAllowedError') {
					return;
				}
				// Other errors - just log, don't show to user for conditional UI
				console.debug('Conditional UI error:', err);
			}
		}

		// Only start conditional UI if we haven't entered a username yet
		if (!username) {
			startConditionalUI();
		}
	}, [supportsAutofill, supportsWebAuthn, username, onSuccess, redirectTo]);

	const handlePasskeyLogin = useCallback(async () => {
		if (!username.trim()) {
			setError('Please enter your username');
			usernameInputRef.current?.focus();
			return;
		}

		setState('loading');
		setError(null);

		try {
			// Abort any conditional UI that might be running
			conditionalAbortController.current?.abort();

			// 1. Get passkey authentication options from server
			const { options, error: optionsError } = await getPasskeyOptions(username);

			if (optionsError || !options) {
				setState('error');
				setError(optionsError || 'Failed to get authentication options');
				return;
			}

			// 2. Prompt user for passkey
			const credential = await startAuthentication({
				optionsJSON: options,
			});

			// 3. Send credential to server
			const result = await loginWithPasskey(username, credential);

			if (result.success && result.token) {
				setAuthToken(result.token);
				setState('success');
				if (onSuccess) {
					onSuccess();
				} else {
					window.location.href = redirectTo;
				}
			} else {
				setState('error');
				setError(result.error || 'Authentication failed');
			}
		} catch (err) {
			setState('error');
			if (err instanceof Error) {
				if (err.name === 'NotAllowedError') {
					setError('Authentication was cancelled or timed out');
				} else if (err.name === 'InvalidStateError') {
					setError('No matching passkey found for this device');
				} else {
					setError(err.message);
				}
			} else {
				setError('An unexpected error occurred');
			}
		}
	}, [username, onSuccess, redirectTo]);

	const handlePasswordLogin = useCallback(async () => {
		if (!username.trim()) {
			setError('Please enter your username');
			usernameInputRef.current?.focus();
			return;
		}

		if (!password) {
			setError('Please enter your password');
			return;
		}

		setState('loading');
		setError(null);

		try {
			const result = await loginWithPassword(username, password);

			if (result.success && result.token) {
				setAuthToken(result.token);
				setState('success');
				if (onSuccess) {
					onSuccess();
				} else {
					window.location.href = redirectTo;
				}
			} else {
				setState('error');
				setError(result.error || 'Login failed');
			}
		} catch (err) {
			setState('error');
			const message = err instanceof Error ? err.message : 'An unexpected error occurred';
			setError(message);
		}
	}, [username, password, onSuccess, redirectTo]);

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			if (mode === 'passkey') {
				handlePasskeyLogin();
			} else {
				handlePasswordLogin();
			}
		},
		[mode, handlePasskeyLogin, handlePasswordLogin]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && mode === 'passkey') {
				e.preventDefault();
				handlePasskeyLogin();
			}
		},
		[mode, handlePasskeyLogin]
	);

	const isLoading = state === 'loading';

	return (
		<div className="min-h-screen flex items-center justify-center p-4 bg-background">
			<Card className="w-full max-w-md border-border">
				<CardHeader className="text-center">
					<div className="text-primary text-xl font-mono">
						&gt; LOGIN<span className="animate-cursor-blink">_</span>
					</div>
					<CardDescription className="font-mono text-muted-foreground">// authenticate to continue</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* Error message */}
						{error && (
							<div className="p-3 border border-destructive bg-destructive/10 text-destructive text-sm font-mono">
								[ERROR] {error}
							</div>
						)}

						{/* Success message */}
						{state === 'success' && (
							<div className="p-3 border border-green-500 bg-green-500/10 text-green-400 text-sm font-mono">
								[OK] Login successful! Redirecting...
							</div>
						)}

						{/* Username field */}
						<div className="space-y-2">
							<Label htmlFor="username" className="font-mono text-muted-foreground">user:</Label>
							<Input
								ref={usernameInputRef}
								id="username"
								type="text"
								placeholder="Enter your username"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								onKeyDown={handleKeyDown}
								disabled={isLoading}
								autoComplete={supportsAutofill ? 'username webauthn' : 'username'}
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck="false"
							/>
						</div>

						{/* Password field (hidden by default) */}
						{mode === 'password' && (
							<div className="space-y-2">
								<Label htmlFor="password" className="font-mono text-muted-foreground">pass:</Label>
								<Input
									id="password"
									type="password"
									placeholder="Enter your password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									disabled={isLoading}
									autoComplete="current-password"
								/>
							</div>
						)}

						{/* Passkey login button */}
						{mode === 'passkey' && (
							<Button
								type="button"
								className="w-full font-mono"
								size="lg"
								onClick={handlePasskeyLogin}
								disabled={isLoading || !supportsWebAuthn}
							>
								{isLoading ? (
									<Loader2 className="size-5 animate-spin" />
								) : (
									<Fingerprint className="size-5" />
								)}
								{isLoading ? 'AUTHENTICATING...' : '[PASSKEY]'}
							</Button>
						)}

						{/* Password submit button */}
						{mode === 'password' && (
							<Button type="submit" className="w-full font-mono" size="lg" disabled={isLoading}>
								{isLoading ? (
									<Loader2 className="size-5 animate-spin" />
								) : (
									<KeyRound className="size-5" />
								)}
								{isLoading ? 'SIGNING IN...' : '[LOGIN]'}
							</Button>
						)}

						{/* Mode toggle */}
						<div className="text-center">
							{mode === 'passkey' ? (
								<button
									type="button"
									onClick={() => {
										setMode('password');
										setError(null);
									}}
									className={cn(
										'text-sm text-muted-foreground hover:text-primary transition-colors font-mono',
										'underline-offset-4 hover:underline'
									)}
									disabled={isLoading}
								>
									// use password
								</button>
							) : (
								<button
									type="button"
									onClick={() => {
										setMode('passkey');
										setError(null);
										setPassword('');
									}}
									className={cn(
										'text-sm text-muted-foreground hover:text-primary transition-colors font-mono',
										'underline-offset-4 hover:underline'
									)}
									disabled={isLoading}
								>
									// use passkey
								</button>
							)}
						</div>

						{/* WebAuthn not supported message */}
						{!supportsWebAuthn && mode === 'passkey' && (
							<p className="text-sm text-muted-foreground text-center font-mono">
								// passkeys not supported{' '}
								<button
									type="button"
									onClick={() => setMode('password')}
									className="text-primary underline underline-offset-4"
								>
									use password
								</button>
							</p>
						)}

						{/* Passkey autofill hint */}
						{supportsAutofill && mode === 'passkey' && !username && (
							<p className="text-xs text-muted-foreground text-center font-mono">
								// passkeys auto-suggest on focus
							</p>
						)}
					</form>
				</CardContent>
			</Card>
		</div>
	);
}

export default LoginPage;
